import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ShieldQuestion, MapPin, Battery, MessageCircle } from "lucide-react";
import AaryaMark from "./AaryaMark";

// "How SOS works" bottom sheet - plain language, no jargon.
// Uses the same fixed inset-0 pattern as Trust/Terms/Privacy so it never
// renders below the viewport when opened from a scrolled Profile screen.
export default function HowSosSheet({ open, onClose }) {
  if (!open) return null;
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 z-50 flex items-end justify-center" onClick={onClose}
      >
        <motion.div
          initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }}
          transition={{ type: "spring", damping: 28, stiffness: 280 }}
          onClick={(e) => e.stopPropagation()}
          className="w-full max-w-[430px] max-h-[92dvh] bg-sand-50 rounded-t-[32px] flex flex-col"
          data-testid="how-sos-sheet"
        >
          <div className="flex items-center justify-between px-6 pt-5 pb-3 border-b border-sand-200">
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-2xl bg-brick-500 text-white flex items-center justify-center"><ShieldQuestion size={18}/></div>
              <div>
                <p className="font-display text-xl text-ink-900 leading-none">How silent SOS works</p>
                <p className="text-[10px] tracking-[0.18em] uppercase text-ink-500 mt-1">Hold 2 seconds. That&apos;s it.</p>
              </div>
            </div>
            <button onClick={onClose} data-testid="how-sos-close"
              className="h-9 w-9 rounded-full bg-white border border-sand-200 flex items-center justify-center">
              <X size={16}/>
            </button>
          </div>

          <div className="overflow-y-auto px-6 py-5 space-y-4">
            <div className="rounded-3xl bg-brick-50 border border-brick-500/30 p-5">
              <p className="text-[11px] tracking-[0.16em] uppercase text-brick-700 font-semibold">The 2-second rule</p>
              <p className="text-[15px] text-ink-900 mt-2 leading-relaxed">
                Press and hold the red bar at the bottom of Home for 2 full seconds. A progress bar fills as you hold - if you release before it completes, nothing happens (this prevents accidental panic).
              </p>
            </div>

            <SosStep n="1" icon={ShieldQuestion} title="You hold for 2 seconds"
              body="No PINs, no unlocks. Aarya knows a long press is an intentional call for help."/>
            <SosStep n="2" icon={MapPin} title="Aarya captures your location"
              body="Just once - the moment you trigger. Not before, not after. A tappable Google Maps link goes with the alert."/>
            <SosStep n="3" icon={Battery} title="Your phone battery is included"
              body="So family knows how long they have to reach you before your phone goes dark."/>
            <SosStep n="4" icon={MessageCircle} title="Trust Circle receives it - WhatsApp first, SMS if not"
              body="Every accepted contact gets the message within seconds. WhatsApp when they&apos;re opted in (free), SMS otherwise. Never both, never noisy."/>

            <div className="rounded-3xl bg-ink-900 text-white p-5">
              <p className="text-[13px] leading-relaxed opacity-90">
                <strong>Silent</strong> means Aarya does not ring, flash, or make a sound - so you can call for help even when it would be dangerous to be seen doing so.
              </p>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

function SosStep({ n, icon: Icon, title, body }) {
  return (
    <div className="rounded-3xl bg-white border border-sand-200 p-5 flex items-start gap-3">
      <div className="h-10 w-10 rounded-full bg-terracotta-500 text-white flex items-center justify-center flex-shrink-0 font-display font-semibold">
        {n}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <Icon size={14} className="text-ink-500"/>
          <p className="text-[14px] font-semibold text-ink-900 leading-tight">{title}</p>
        </div>
        <p className="text-[13px] text-ink-600 mt-1.5 leading-relaxed">{body}</p>
      </div>
    </div>
  );
}
